<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useProductPlansStore } from 'dashboard/stores/productPlans';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const props = defineProps({
  product: { type: Object, required: true },
});

const { t } = useI18n();
const store = useProductPlansStore();
const uiFlags = computed(() => store.uiFlags);
const plans = computed(() =>
  store.records.filter(p => p.product_id === props.product.id)
);

const recurringIntervalOptions = computed(() => [
  { value: 'none', label: 'One-time only' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
]);

const showFormModal = ref(false);
const showDeleteModal = ref(false);
const selectedPlan = ref(null);

const emptyForm = () => ({
  name: '',
  description: '',
  setup_price: '0',
  recurring_price: '0',
  recurring_interval: 'monthly',
  gst_percent: '18',
  implementation_timeline: '',
  implementation_fee: '0',
  active: true,
  featuresText: '',
});
const form = reactive(emptyForm());
const isFormInvalid = computed(() => !form.name.trim());

const openAdd = () => {
  selectedPlan.value = null;
  Object.assign(form, emptyForm());
  showFormModal.value = true;
};

const openEdit = plan => {
  selectedPlan.value = plan;
  Object.assign(form, {
    name: plan.name,
    description: plan.description || '',
    setup_price: String(plan.setup_price),
    recurring_price: String(plan.recurring_price),
    recurring_interval: plan.recurring_interval,
    gst_percent: String(plan.gst_percent),
    implementation_timeline: plan.implementation_timeline || '',
    implementation_fee: String(plan.implementation_fee || 0),
    active: plan.active,
    featuresText: (plan.features || []).join('\n'),
  });
  showFormModal.value = true;
};

const closeFormModal = () => {
  showFormModal.value = false;
};

const submitForm = async () => {
  if (isFormInvalid.value) return;
  const { featuresText, ...rest } = form;
  const payload = {
    ...rest,
    product_id: props.product.id,
    features: featuresText
      .split('\n')
      .map(f => f.trim())
      .filter(Boolean),
  };
  try {
    if (selectedPlan.value) {
      await store.update({ id: selectedPlan.value.id, ...payload });
    } else {
      await store.create(payload);
    }
    useAlert(t('PRODUCTS_MGMT.PLANS.SAVED'));
    closeFormModal();
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_MGMT.PLANS.SAVE_ERROR'));
  }
};

const openDelete = plan => {
  selectedPlan.value = plan;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  try {
    await store.delete(selectedPlan.value.id);
    useAlert(t('PRODUCTS_MGMT.PLANS.DELETED'));
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_MGMT.PLANS.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};

watch(
  () => props.product?.id,
  productId => {
    if (productId) store.get({ productId });
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('PRODUCTS_MGMT.PLANS.TITLE') }}
        <span class="text-sm font-normal text-n-slate-11">{{
          t('PRODUCTS_MGMT.PLANS.FOR_PRODUCT', { product: product.name })
        }}</span>
      </h3>
      <Button
        :label="t('PRODUCTS_MGMT.PLANS.ADD')"
        size="sm"
        @click="openAdd"
      />
    </div>
    <BaseTable
      :headers="[
        t('PRODUCTS_MGMT.PLANS.TITLE'),
        t('PRODUCTS_MGMT.PLANS.SETUP_PRICE'),
        t('PRODUCTS_MGMT.PLANS.RECURRING_PRICE'),
        t('PRODUCTS_MGMT.PLANS.GST_PERCENT'),
        '',
      ]"
      :items="plans"
      :no-data-message="t('PRODUCTS_MGMT.PLANS.NO_DATA')"
    >
      <template #row="{ items }">
        <BaseTableRow v-for="plan in items" :key="plan.id" :item="plan">
          <template #default>
            <BaseTableCell>
              <span class="text-body-main text-n-slate-12">{{
                plan.name
              }}</span>
            </BaseTableCell>
            <BaseTableCell>{{ plan.setup_price }}</BaseTableCell>
            <BaseTableCell>
              {{
                plan.recurring_interval === 'none'
                  ? '-'
                  : `${plan.recurring_price} / ${plan.recurring_interval}`
              }}
            </BaseTableCell>
            <BaseTableCell>{{ plan.gst_percent }}%</BaseTableCell>
            <BaseTableCell align="end">
              <div class="flex justify-end gap-2">
                <Button
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  @click="openEdit(plan)"
                />
                <Button icon="i-woot-bin" slate sm @click="openDelete(plan)" />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <woot-modal v-model:show="showFormModal" :on-close="closeFormModal">
      <div class="flex flex-col w-full gap-4 p-6 overflow-y-auto max-h-[80vh]">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{
            selectedPlan
              ? t('PRODUCTS_MGMT.PLANS.EDIT')
              : t('PRODUCTS_MGMT.PLANS.ADD')
          }}
        </h3>
        <Input
          v-model="form.name"
          :placeholder="t('PRODUCTS_MGMT.PLANS.NAME_PLACEHOLDER')"
          autofocus
        />
        <TextArea
          v-model="form.description"
          :placeholder="t('PRODUCTS_MGMT.SECTORS.DESCRIPTION_PLACEHOLDER')"
        />
        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="form.setup_price"
            type="number"
            :label="t('PRODUCTS_MGMT.PLANS.SETUP_PRICE')"
          />
          <Input
            v-model="form.gst_percent"
            type="number"
            :label="t('PRODUCTS_MGMT.PLANS.GST_PERCENT')"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <SelectInput
            v-model="form.recurring_interval"
            :options="recurringIntervalOptions"
          />
          <Input
            v-model="form.recurring_price"
            type="number"
            :label="t('PRODUCTS_MGMT.PLANS.RECURRING_PRICE')"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="form.implementation_fee"
            type="number"
            :label="t('PRODUCTS_MGMT.PLANS.IMPLEMENTATION_FEE')"
          />
          <Input
            v-model="form.implementation_timeline"
            :label="t('PRODUCTS_MGMT.PLANS.IMPLEMENTATION_TIMELINE')"
            :placeholder="
              t('PRODUCTS_MGMT.PLANS.IMPLEMENTATION_TIMELINE_PLACEHOLDER')
            "
          />
        </div>
        <TextArea
          v-model="form.featuresText"
          :placeholder="t('PRODUCTS_MGMT.PLANS.FEATURES_PLACEHOLDER')"
          :label="t('PRODUCTS_MGMT.PLANS.FEATURES')"
        />
        <div class="flex items-center gap-2">
          <Switch v-model="form.active" />
          <span class="text-sm text-n-slate-11">{{
            t('COMMON_MGMT.ACTIVE')
          }}</span>
        </div>
        <div class="flex justify-end gap-2">
          <Button
            :label="t('COMMON_MGMT.CANCEL')"
            variant="link"
            @click="closeFormModal"
          />
          <Button
            :label="t('COMMON_MGMT.SAVE')"
            :disabled="isFormInvalid"
            :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
            @click="submitForm"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="confirmDelete"
      :title="t('PRODUCTS_MGMT.PLANS.DELETE_TITLE')"
      :message="t('PRODUCTS_MGMT.PLANS.DELETE_MESSAGE')"
      :message-value="selectedPlan?.name"
      :confirm-text="t('COMMON_MGMT.DELETE')"
      :reject-text="t('COMMON_MGMT.CANCEL')"
    />
  </div>
</template>
